import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Button as MuiButton,
  Collapse,
  Stack
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { ReactComponent as PersonAddIcon } from '../../../common/icons/addUserIcon.svg'
import React from 'react'
import { OnboardingStepStatus } from '../../../api/onboardingApiSlice'

const getIcon = iconName => {
  if (iconName === 'PersonAddIcon') {
    return <PersonAddIcon />
  }
  return null
}

export function StepListItem({ step, onSelectStep, isActive, onOpenDrawer, isDisabled, onCompleteStep }) {
  const isStepCompleted = step.status === OnboardingStepStatus.COMPLETED

  const handleListItemClick = () => {
    if (!isDisabled && onSelectStep) {
      onSelectStep(step.id)
    }
  }

  const handleActionButtonClick = e => {
    e.stopPropagation()
    if (onOpenDrawer && step.drawerType && !isStepCompleted && !isDisabled) {
      onOpenDrawer(step.drawerType)
    }
  }

  const handleSecondaryActionButtonClick = e => {
    e.stopPropagation()
    if (onCompleteStep && step.apiType && !isStepCompleted && !isDisabled) {
      onCompleteStep(step.apiType, true)
    } else if (!isStepCompleted && !isDisabled && onOpenDrawer && step.secondaryActionDrawerType) {
      onOpenDrawer(step.secondaryActionDrawerType)
    }
  }

  const startIcon =
    step.actionButtonStartIcon && typeof getIcon === 'function' ? getIcon(step.actionButtonStartIcon) : null

  const dynamicListItemStyles = { ...getListItemSx(isActive, isStepCompleted) }
  if (isDisabled && !isStepCompleted) {
    dynamicListItemStyles.opacity = 0.5
    dynamicListItemStyles.pointerEvents = 'none'
    dynamicListItemStyles.cursor = 'not-allowed'
  } else if (!isStepCompleted && onSelectStep && !isDisabled) {
    dynamicListItemStyles.cursor = 'pointer'
  } else if (isStepCompleted && onOpenDrawer && step.drawerType) {
    dynamicListItemStyles.cursor = 'pointer'
  } else {
    dynamicListItemStyles.cursor = 'default'
  }

  return (
    <ListItem sx={dynamicListItemStyles} onClick={handleListItemClick}>
      <Box display='flex' alignItems='center' width='100%'>
        <ListItemIcon sx={{ minWidth: 'auto' }}>
          <Box sx={getNumberCircleSx(isActive, isStepCompleted, isDisabled)}>
            {isStepCompleted ? <CheckIcon fontSize='small' /> : step.id}
          </Box>
        </ListItemIcon>
        <ListItemText
          primary={step.title}
          primaryTypographyProps={{
            sx: getTitleSx(isActive, isStepCompleted, isDisabled)
          }}
        />
      </Box>

      <Collapse in={isActive && !isDisabled} timeout='auto' unmountOnExit sx={{ width: '100%', pl: '44px' }}>
        <Typography sx={descriptionSx}>{step.description}</Typography>

        <Stack direction='row' spacing={1} sx={{ mt: 1.5, alignItems: 'center' }}>
          {isStepCompleted && !isDisabled && step.drawerType && onOpenDrawer && (
            <MuiButton
              variant='contained'
              size='small'
              startIcon={startIcon}
              onClick={e => {
                e.stopPropagation()
                onOpenDrawer(step.drawerType)
              }}
              sx={primaryActionButtonSx}
            >
              {step.actionButtonText || 'View Details'}
            </MuiButton>
          )}

          {!isStepCompleted && !isDisabled && (
            <>
              {step.actionButtonText && step.drawerType && (
                <MuiButton
                  variant='contained'
                  size='small'
                  onClick={handleActionButtonClick}
                  sx={primaryActionButtonSx}
                  startIcon={startIcon}
                >
                  {step.actionButtonText}
                </MuiButton>
              )}
              {step.secondaryActionButtonText && (
                <MuiButton
                  variant='outlined'
                  size='small'
                  sx={secondaryActionButtonSx}
                  onClick={handleSecondaryActionButtonClick}
                >
                  {step.secondaryActionButtonText}
                </MuiButton>
              )}
              {!step.secondaryActionButtonText && step.id !== 1 && step.apiType && onCompleteStep && (
                <MuiButton
                  variant='outlined'
                  size='small'
                  sx={secondaryActionButtonSx}
                  onClick={e => {
                    e.stopPropagation()
                    onCompleteStep(step.apiType, true)
                  }}
                >
                  Skip
                </MuiButton>
              )}
            </>
          )}
        </Stack>
      </Collapse>
    </ListItem>
  )
}

const numberCircleBaseSx = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  mr: 2,
  fontWeight: 'bold',
  fontSize: '14px',
  flexShrink: 0
}

const getNumberCircleSx = (isActive, isCompleted, isDisabled) => {
  if (isDisabled && !isCompleted) {
    return {
      ...numberCircleBaseSx,
      backgroundColor: 'action.disabledBackground',
      color: 'text.disabled'
    }
  }
  if (isCompleted) {
    return {
      ...numberCircleBaseSx,
      backgroundColor: 'action.disabledBackground',
      color: 'action.disabled'
    }
  }
  if (isActive) {
    return {
      ...numberCircleBaseSx,
      backgroundColor: 'primary.main',
      color: 'primary.contrastText'
    }
  }
  return {
    ...numberCircleBaseSx,
    backgroundColor: 'grey.300',
    color: 'text.primary'
  }
}

const getListItemSx = (isActive, isCompleted) => ({
  mb: 1.5,
  p: 1.5,
  borderRadius: '8px',
  backgroundColor: isActive && !isCompleted ? 'action.hover' : 'transparent',
  transition: 'background-color 0.2s, border-color 0.2s, opacity 0.2s',
  flexDirection: 'column',
  alignItems: 'flex-start',
  '&:hover': {
    backgroundColor:
      !isCompleted && !isActive ? 'action.selected' : isActive && !isCompleted ? 'action.hover' : undefined
  }
})

const getTitleSx = (isActive, isCompleted, isDisabled) => ({
  fontWeight: 'bold',
  fontFamily: 'Plus Jakarta Sans',
  fontSize: '20px',
  color: isDisabled || isCompleted || !isActive ? 'text.disabled' : 'text.primary',
  mb: 0.5,
  textDecoration: 'none'
})

const descriptionSx = {
  fontSize: '16px',
  fontFamily: 'Plus Jakarta Sans',
  color: '#76767D',
  mb: 2,
  whiteSpace: 'normal',
  wordBreak: 'break-word'
}

const baseButtonSx = {
  textTransform: 'none',
  fontWeight: '400',
  fontSize: '16px',
  width: '180px',
  fontFamily: 'Plus Jakarta Sans',
  py: 1,
  px: 1,
  borderRadius: '12px'
}

const primaryActionButtonSx = {
  ...baseButtonSx,
  backgroundColor: '#0246F8',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#0032b8'
  }
}

const secondaryActionButtonSx = {
  ...baseButtonSx
}
